import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthModel, UserModel, UserModelLogin } from './_models'
import * as authHelper from './AuthHelpers'
import { getCurrentUser, sendTokenRequest } from './_requests'
import { WithChildren } from '../../../../_metronic/helpers'
import { localStorageItem } from "../../utils/localStorage";
import jwt_decode from "jwt-decode";
import { AUTHORIZE_REQUEST, KEY_LOCALSTORAGE } from "./_consts";
import { useLocation, useNavigate } from 'react-router-dom'
import { RESPONSE_STATUS_CODE } from '../../constants/moduleConsts'
import { getEmployeeIdByUsername } from '../../profile/services/dialogServices'
import { toast } from 'react-toastify'
import useMultiLanguage from '../../../hook/useMultiLanguage'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | UserModelLogin | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | UserModelLogin | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => { },
  currentUser: undefined,
  setCurrentUser: () => { },
  logout: () => { },
}

export const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | UserModelLogin | undefined>()

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.logoutAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  return (
    <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, saveAuth, setCurrentUser } = useAuth();
  const { lang } = useMultiLanguage();
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const code = new URL(window.location.href).searchParams.get("code");
  const navigate = useNavigate();
  useEffect(() => {
    init();
  }, [])

  const requestUser = async (auth: AuthModel) => {
    try {
      if (!didRequest.current) {
        const idTokenDecode = await jwt_decode(auth?.id_token) as {
          authorities: string[],
        }
        const accessTokenDecode = await jwt_decode(auth?.access_token) as {
          scope: string[],
        }

        if (idTokenDecode || accessTokenDecode) {
          try {
            localStorageItem.set(KEY_LOCALSTORAGE.ID_TOKEN_DECODE, idTokenDecode)
            localStorageItem.set(KEY_LOCALSTORAGE.ACCESS_TOKEN_DECODE, accessTokenDecode)
            if (idTokenDecode?.authorities) {
              const permissionObj: { [key: string]: boolean; } = {};
              for (const permission of idTokenDecode.authorities) {
                permissionObj[permission] = true;
              }
              localStorageItem.set(KEY_LOCALSTORAGE.AUTHORITIES, permissionObj)
            }
          } catch {

          } finally {
            authHelper.setCurrentUser(idTokenDecode);
          }
        }
        try {
          const { data } = await getCurrentUser();
          if(data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
            setCurrentUser(data?.data as UserModel | UserModelLogin | undefined);
            localStorageItem.set(KEY_LOCALSTORAGE.CURRENT, data?.data);

            const { data: dataEmployee } = await getEmployeeIdByUsername((data?.data as any)?.username);
            if(dataEmployee?.code === RESPONSE_STATUS_CODE.SUCCESS) {
              setCurrentUser(dataEmployee?.data);
              localStorageItem.set(KEY_LOCALSTORAGE.EMPLOYEE, dataEmployee?.data);
            } else {
              toast.warning(dataEmployee?.message);
            }
          } else {
            toast.warning(data?.message);
          }
        } catch (error) {
          console.error(error);
          toast.error(lang("GENERAL.ERROR"));
        }
      }
    } catch (error) {
      console.error(error)
      if (!didRequest.current) {
        window.location.href = AUTHORIZE_REQUEST
      }
    } finally {
      setShowSplashScreen(false)
    }

    return () => (didRequest.current = true)
  }

  const init = async () => {
    await authHelper.handleGetServiceConfiguration()
    if (process.env.REACT_APP_SSO_ENABLE === "true") {
      if (code && !auth) {
        sendTokenRequest(code).then((resAuth) => {
          saveAuth(resAuth);
          requestUser(resAuth);
          const beforeLoginUrl = sessionStorage.getItem('beforeLogin');
          sessionStorage.removeItem('beforeLogin');
          navigate(beforeLoginUrl ?? "");
        })
          .catch(() => {
            window.location.href = `${localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.["ssoUrl"]}/${AUTHORIZE_REQUEST}`
          })
      } else if (!auth) {
        sessionStorage.setItem('beforeLogin', window.location.pathname);
        window.location.href = `${localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.["ssoUrl"]}/${AUTHORIZE_REQUEST}`
      } else {
        requestUser(auth);
      }
    } else {
      if (auth && auth.access_token) {
        requestUser(auth)
      } else {
        window.location.href = `${localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.["ssoUrl"]}/${AUTHORIZE_REQUEST}`
      }
    }
  };

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
